import { Broadcast } from '../../../../models/broadcast';
import { channelSortIndex } from '../../channel-order';

/**
 * Pure geometry for the channel-column Programmplan grid. The Programmtag
 * runs 06:00 → 05:59(+1); the backend's ProgrammDate query guarantees every
 * returned row falls into that window, so the vertical axis is exactly
 * 24 hours anchored at 06:00.
 */
export const HOUR_HEIGHT_PX = 140;                 // 10-min Sendung ≈ 23px ≈ one text line
export const LANE_HEIGHT_PX = 24 * HOUR_HEIGHT_PX; // 3360
export const MIN_BLOCK_PX = 18;
export const LINE_HEIGHT_PX = 15;
export const BLOCK_V_PADDING_PX = 6;               // block padding + border, see .program-block

export interface PositionedBroadcast extends Broadcast {
  topPx: number;
  heightPx: number;
  /** How many text lines fit into heightPx — drives -webkit-line-clamp. */
  lineClamp: number;
}

export interface ChannelColumn {
  channel: string;
  /**
   * Chronological within the Programmtag. Every block spans the full lane
   * width; overlapping broadcasts stack — the array (= DOM) order makes the
   * LATER broadcast paint on top, and :hover raises a block above its
   * neighbours so partially covered ones can be inspected.
   */
  broadcasts: PositionedBroadcast[];
}

export function isoDurationToMinutes(duration: string): number {
  if (!duration) return 0;

  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;

  const match = duration.match(regex);
  if (!match) return 0;

  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  const seconds = parseInt(match[3] || "0", 10);

  return hours * 60 + minutes + seconds / 60;
}

/**
 * Minute offset of a start time within the Programmtag: local wall-clock
 * minus 06:00, wrapped into [0, 1440) so early-morning next-day slots land
 * at the bottom of the grid.
 */
export function programmtagStartMinute(anchorIso: string): number {
  const d = new Date(anchorIso);
  let m = d.getHours() * 60 + d.getMinutes() - 6 * 60;
  if (m < 0) m += 24 * 60;
  return m;
}

/**
 * Programmtag date of a broadcast key, e.g.
 * 'LIVE/2026/0724 0530/ORF2/broadcast.json' → '2026-07-23'.
 * Mirrors the backend's ProgrammDate derivation exactly (VpsCode wall time
 * minus 6h, pure calendar math — timezone independent), so subscription
 * payloads can be matched against the selected day without replicating
 * server timezone rules.
 */
export function programmtagDateFromKey(key: string): string | null {
  const m = /^LIVE\/(\d{4})\/(\d{2})(\d{2}) (\d{2})(\d{2})\//.exec(key ?? '');
  if (!m) return null;
  const wallTimeAsUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  return new Date(wallTimeAsUtc - 6 * 3600_000).toISOString().slice(0, 10);
}

/**
 * Groups the day's broadcasts into ordered channel columns and positions
 * each block on the 24h axis. Pure: input objects are not mutated — every
 * positioned block is a copy (the component's click/hover/details flow
 * already works on copies).
 */
export function buildChannelColumns(
  broadcasts: Broadcast[],
  selectedChannels: string[],
  showLive: boolean,
  showVOD: boolean,
): ChannelColumn[] {
  const byChannel = new Map<string, Broadcast[]>();
  for (const b of broadcasts) {
    if (!selectedChannels.includes(b.channel)) continue;
    if (!(showLive && b.live || showVOD && !b.live)) continue;
    const group = byChannel.get(b.channel);
    if (group) group.push(b); else byChannel.set(b.channel, [b]);
  }

  const channels = Array.from(byChannel.keys()).sort((a, z) =>
    channelSortIndex(a) - channelSortIndex(z) || a.localeCompare(z));

  return channels.map(channel => {
    const positioned = byChannel.get(channel)!
      .map(b => {
        const anchor = b.startTime ?? b.ddsStartsAt;
        const startMin = anchor ? programmtagStartMinute(anchor) : 0;
        return { b, startMin, endMin: startMin + isoDurationToMinutes(b.bruttoDuration) };
      })
      // Chronological; at identical starts the LONGER Sendung first so the
      // shorter one renders later and stays visible on top of it.
      .sort((a, z) => a.startMin - z.startMin || z.endMin - a.endMin)
      .map(({ b, startMin, endMin }) => {
        let topPx = Math.round((startMin / 60) * HOUR_HEIGHT_PX);
        if (topPx > LANE_HEIGHT_PX - MIN_BLOCK_PX) topPx = LANE_HEIGHT_PX - MIN_BLOCK_PX;
        let heightPx = Math.max(Math.round(((endMin - startMin) / 60) * HOUR_HEIGHT_PX), MIN_BLOCK_PX);
        if (topPx + heightPx > LANE_HEIGHT_PX) {
          heightPx = Math.max(LANE_HEIGHT_PX - topPx, MIN_BLOCK_PX);
        }
        return {
          ...b,
          // Actual airtime when known, for display/tooltip/details — the
          // position anchor stays the SCHEDULED start (see sort above).
          startsAt: b.ddsStartsAt ?? b.startTime,
          topPx,
          heightPx,
          lineClamp: Math.max(1, Math.floor((heightPx - BLOCK_V_PADDING_PX) / LINE_HEIGHT_PX)),
        };
      });

    return { channel, broadcasts: positioned };
  });
}
